import styles from './styles.module.scss';
import { VHCRecordCheckResultDto } from 'api/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useSendSmsQuotesMutation } from 'features/vhc/api/use-send-sms-quotes.mutation';
import { useVhcRecordsAuthorizationEmailMutation } from 'features/vhc/api/use-vhc-records-authorization-email.mutation';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { formatDate } from 'utils/formats';

type ButtonsGroupProps = {
  hasQuote?: boolean;
  checkResult: Array<VHCRecordCheckResultDto>;
  soHeaderId?: number;
  dateCompleted?: string | null;
  dateSentAuthorizationRequiredEmail?: string | null;
  userSentAuthorizationRequiredEmail?: string | null;
  dateSentAuthorizationRequiredSms?: string | null;
  userSentAuthorizationRequiredSms?: string | null;
  refetch?: () => void;
};

export const ButtonsGroup: FC<ButtonsGroupProps> = ({
  hasQuote,
  soHeaderId,
  checkResult,
  dateCompleted,
  dateSentAuthorizationRequiredEmail,
  userSentAuthorizationRequiredEmail,
  dateSentAuthorizationRequiredSms,
  userSentAuthorizationRequiredSms,
  refetch
}) => {
  const { id: vhcRecordId } = useParams();
  const { mutate: sendEmail, isLoading: isLoadingEmail } = useVhcRecordsAuthorizationEmailMutation();
  const { mutate: sendSms, isLoading: isLoadingSms } = useSendSmsQuotesMutation();

  const handleSendCustomerEmail = () => {
    if (!vhcRecordId) return;

    const id = Number(vhcRecordId);

    if (isNaN(id)) return;

    sendEmail(id, {
      onSuccess: () => {
        refetch && refetch();
      }
    });
  };

  const handleSendCustomerSms = () => {
    if (!soHeaderId) return;

    const id = Number(soHeaderId);

    if (isNaN(id)) return;

    sendSms(
      {
        soHeaderId: soHeaderId
      },
      {
        onSuccess: () => {
          refetch && refetch();
        }
      }
    );
  };

  const hasWarningOrNotOk = checkResult?.some((item) => item.conditionId === 3 || item.conditionId === 4);

  if (!hasQuote && hasWarningOrNotOk) return null;

  if (!hasQuote && !hasWarningOrNotOk && !dateCompleted) return null;

  return (
    <div className={styles.buttonGroup}>
      <button
        type="button"
        onClick={() => {
          window.open(`https://advancevauxhall.co.uk/repair-order-quote/${soHeaderId}`, '_blank');
        }}
        className="btn btn-secondary text-nowrap w-100 w-md-auto md-sm-0"
        style={{ minWidth: '213px' }}
      >
        <InlineSvg path="/media/icons/custom/exit-right-corner.svg" className="svg-icon-2" />
        Client Link
      </button>
      <div>
        <button
          onClick={() => handleSendCustomerEmail()}
          className="btn btn-secondary text-nowrap w-100 w-md-auto"
          type="button"
          disabled={isLoadingEmail}
        >
          {!isLoadingEmail ? (
            <>
              <InlineSvg path="/media/icons/custom/sms.svg" className="svg-icon-2" />
              {dateSentAuthorizationRequiredEmail?.length ? 'Customer Email Sent ' : 'Send Customer Email'}
            </>
          ) : (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
        {dateSentAuthorizationRequiredEmail?.length && (
          <div className={styles.sentEmailInfo}>
            <div>{userSentAuthorizationRequiredEmail}</div>
            <div>
              {dateSentAuthorizationRequiredEmail
                ? formatDate(dateSentAuthorizationRequiredEmail, 'DD MMM YYYY - HH:mm')
                : '-'}
            </div>
          </div>
        )}
      </div>
      <div>
        <button
          onClick={() => handleSendCustomerSms()}
          className="btn btn-secondary text-nowrap w-100 w-md-auto"
          type="button"
          disabled={isLoadingSms}
        >
          {!isLoadingSms ? (
            <>
              <InlineSvg path="/media/icons/custom/sms.svg" className="svg-icon-2" />
              SMS Customer
            </>
          ) : (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
        {dateSentAuthorizationRequiredSms?.length && (
          <div className={styles.sentSmsInfo}>
            <div>{userSentAuthorizationRequiredSms ?? ''}</div>
            <div>
              {dateSentAuthorizationRequiredSms
                ? formatDate(dateSentAuthorizationRequiredSms, 'DD MMM YYYY - HH:mm')
                : '-'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
